import { format, isValid as isValidDate, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { type MouseEvent, useCallback, useMemo } from "react";
import { useTranslations } from "../../i18n";
import { clsx } from "clsx";
import { IconButton } from "../Button/IconButton";
import { useDropdownContext } from "../Dropdown";
import type { IInputSize } from "../Input";
import { IMask, Input } from "../Input";
import { useIDatePickerContext } from "./context";
import type { IDatePickerTriggerProps } from "./types";
import { textFieldButtonColor, textFieldButtonSize } from "../../utils";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export const DatePickerTrigger = (props: IDatePickerTriggerProps = {}) => {
  const {
    disabled,
    loading,
    invalid,
    color = "layer1",
    placeholder,
    autoComplete,
    onChange,
    onClear,
    setOpen,
    displayValue,
    setDisplayValue,
    inputId,
    minDate,
    maxDate,
    ...contextProps
  } = useIDatePickerContext();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: IInputSize = contextProps.size || (isMobile ? "lg" : "md");
  const { t } = useTranslations();
  const { open, contentId } = useDropdownContext();

  const {
    ref: referenceRef,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    onPointerDown,
    onPointerEnter,
    id: referenceId,
    className,
    role: _role,
    tabIndex: _tabIndex,
    "aria-expanded": _ariaExpanded,
    "aria-haspopup": _ariaHaspopup,
    "aria-controls": _ariaControls,
    ...restReferenceProps
  } = props;

  const parseMaybe = useCallback((s: string): Date | undefined => {
    if (s.trim().length < 10) return undefined;
    const d = parse(s, "MM/dd/yyyy", new Date());
    return isValidDate(d) ? d : undefined;
  }, []);

  const handleInputChange = useCallback(
    (value: string) => {
      setDisplayValue(value);
      onChange(parseMaybe(value));
    },
    [setDisplayValue, onChange, parseMaybe],
  );

  const handleInputBlur = useCallback(() => {
    const parsedDate = parseMaybe(displayValue);

    if (!parsedDate || (!minDate && !maxDate)) {
      return;
    }

    let clampedDate = parsedDate;

    if (minDate && parsedDate < minDate) {
      clampedDate = minDate;
    } else if (maxDate && parsedDate > maxDate) {
      clampedDate = maxDate;
    }

    if (clampedDate !== parsedDate) {
      const formattedValue = format(clampedDate, "MM/dd/yyyy");
      setDisplayValue(formattedValue);
      onChange(clampedDate);
    }
  }, [displayValue, parseMaybe, minDate, maxDate, setDisplayValue, onChange]);

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  }, [setOpen, disabled]);

  const handleClear = onClear
    ? (e?: MouseEvent) => {
        e?.stopPropagation();
        onClear();
      }
    : undefined;

  const mask = useMemo(
    () => ({
      mask: "MM/DD/YYYY",
      blocks: {
        MM: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
        },
        DD: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
        },
        YYYY: {
          mask: IMask.MaskedRange,
          from: 1900,
          to: 2100,
          maxLength: 4,
        },
      },
    }),
    [],
  );

  const dataAttributes = Object.fromEntries(
    Object.entries(contextProps).filter(([key]) => key.startsWith("data-")),
  );

  return (
    <div
      ref={referenceRef}
      id={referenceId}
      className={clsx("nuco-datepicker__trigger", className)}
      tabIndex={-1}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      {...restReferenceProps}
    >
      <Input
        id={inputId}
        {...dataAttributes}
        size={size}
        color={color}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onClear={handleClear}
        disabled={disabled}
        loading={loading}
        invalid={invalid}
        placeholder={placeholder || t("ui.datePicker.placeholder")}
        autoComplete={autoComplete || "off"}
        inputMode="numeric"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={contentId}
        mask={mask}
        tabIndex={disabled ? -1 : 0}
        endAccessory={
          <IconButton
            size={textFieldButtonSize[size]}
            color={textFieldButtonColor[color]}
            tabIndex={disabled ? -1 : 0}
            content={<CalendarIcon aria-hidden="true" />}
            aria-label={
              open
                ? t("ui.datePicker.closeCalendar")
                : t("ui.datePicker.openCalendar")
            }
            onClick={toggleDropdown}
          />
        }
      />
    </div>
  );
};
